// src/utils/printUtils.js - Updated with optimized print CSS

// Number to words conversion for Indian currency
const convertNumberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  if (num === 0) return 'Zero Rupees';

  const convertHundreds = (n) => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    } else if (n >= 10) {
      result += teens[n - 10] + ' ';
      return result;
    }
    if (n > 0) {
      result += ones[n] + ' ';
    }
    return result;
  };

  let rupees = Math.floor(num);
  let paise = Math.round((num - rupees) * 100);

  let result = '';

  if (rupees >= 10000000) { // Crore
    result += convertHundreds(Math.floor(rupees / 10000000)) + 'Crore ';
    rupees %= 10000000;
  }
  if (rupees >= 100000) { // Lakh
    result += convertHundreds(Math.floor(rupees / 100000)) + 'Lakh ';
    rupees %= 100000;
  }
  if (rupees >= 1000) { // Thousand
    result += convertHundreds(Math.floor(rupees / 1000)) + 'Thousand ';
    rupees %= 1000;
  }
  if (rupees > 0) {
    result += convertHundreds(rupees);
  }

  result += 'Rupees';

  if (paise > 0) {
    result += ' and ' + convertHundreds(paise) + 'Paise';
  }

  return result.trim();
};

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount || 0);
};

export const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Helper function to calculate CGST and SGST
const calculateCGSTSGST = (taxableAmount, gstRate) => {
  const totalGSTAmount = (taxableAmount * gstRate) / 100;
  const cgstAmount = totalGSTAmount / 2;
  const sgstAmount = totalGSTAmount / 2;
  return { cgstAmount, sgstAmount, totalGSTAmount };
};

// Get GST breakdown by rate for sales invoices
const getSalesGstBreakdown = (invoice) => {
  if (!invoice?.items || invoice.invoiceType !== 'with_gst') return {};
  
  const gstBreakdown = {};
  const discountPercentage = parseFloat(invoice.discount) || 0;
  
  invoice.items.forEach(item => {
    const rate = item.gstRate || 0;
    const itemDiscountAmount = (item.amount || 0) * discountPercentage / 100;
    const itemTaxableAmount = (item.amount || 0) - itemDiscountAmount;
    const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, rate);

    if (rate > 0) {
      if (!gstBreakdown[rate]) {
        gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
      }
      gstBreakdown[rate].taxableAmount += itemTaxableAmount;
      gstBreakdown[rate].cgstAmount += cgstAmount;
      gstBreakdown[rate].sgstAmount += sgstAmount;
      gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
    }
  });
  return gstBreakdown;
};

// Get GST breakdown for purchase invoices
const getPurchaseGstBreakdown = (invoice) => {
  if (!invoice?.items || invoice.gstType !== 'with_gst') return {};
  
  const gstBreakdown = {};
  
  invoice.items.forEach(item => {
    const rate = item.gstRate || 0;
    const itemAmount = parseFloat(item.amount) || 0;
    const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemAmount, rate);

    if (rate > 0) {
      if (!gstBreakdown[rate]) {
        gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
      }
      gstBreakdown[rate].taxableAmount += itemAmount;
      gstBreakdown[rate].cgstAmount += cgstAmount;
      gstBreakdown[rate].sgstAmount += sgstAmount;
      gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
    }
  });
  return gstBreakdown;
};

// Get payment terms display
const getPaymentTermsDisplay = (terms, isPaid = false) => {
  if (isPaid) return 'Paid';
  
  const displayTerms = {
    immediate: 'Paid',
    net_15: 'Net 15 days',
    net_30: 'Net 30 days',
    net_60: 'Net 60 days'
  };
  return displayTerms[terms] || terms;
};

// Optimized print CSS for all invoice types
const getOptimizedPrintCSS = () => `
<style>
  * {
    -webkit-print-color-adjust: exact !important;
    color-adjust: exact !important;
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: Arial, sans-serif;
    font-size: 12px;
    line-height: 1.3;
    color: #000;
    background: white;
    padding: 15px;
    margin: 0;
  }
  
  .print-content {
    max-width: 800px;
    margin: 0 auto;
    background: white;
    padding: 20px;
  }
  
  /* Page setup for A4 */
  @page {
    size: A4;
    margin: 0.4in;
  }
  
  /* Header styling */
  .invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 20px;
  }
  
  .invoice-title {
    font-size: 24px;
    font-weight: bold;
    color: #000;
    margin-bottom: 8px;
  }
  
  .invoice-meta {
    font-size: 11px;
    line-height: 1.4;
    color: #333;
  }
  
  .vendor-details {
    text-align: right;
    max-width: 280px;
  }
  
  .vendor-name {
    font-size: 16px;
    font-weight: bold;
    margin-bottom: 6px;
    color: #000;
  }
  
  .vendor-info {
    font-size: 11px;
    line-height: 1.4;
    color: #333;
  }
  
  /* Customer/Supplier details */
  .details-section {
    margin-bottom: 20px;
  }
  
  .section-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #000;
  }
  
  .details-box {
    background-color: #f8f9fa;
    padding: 12px;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
  
  .customer-name {
    font-weight: 500;
    font-size: 12px;
    margin-bottom: 3px;
    color: #000;
  }
  
  .customer-info {
    font-size: 11px;
    line-height: 1.4;
    color: #333;
  }
  
  /* Items table - optimized for print */
  .items-section {
    margin-bottom: 20px;
  }
  
  .items-table {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #dee2e6;
    margin: 12px 0;
    font-size: 10px;
  }
  
  .items-table th {
    background-color: #f8f9fa;
    color: #333;
    font-weight: 600;
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    padding: 6px 3px;
    text-align: center;
    border-right: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
  }
  
  .items-table th:last-child {
    border-right: none;
  }
  
  .items-table td {
    padding: 6px 3px;
    border-right: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    font-size: 10px;
    vertical-align: top;
  }
  
  .items-table td:last-child {
    border-right: none;
  }
  
  .item-description {
    font-weight: 500;
    margin-bottom: 1px;
    color: #000;
    font-size: 10px;
  }
  
  .item-details {
    font-size: 8px;
    color: #666;
  }
  
  /* Text alignment */
  .text-center { text-align: center; }
  .text-right { text-align: right; }
  .text-left { text-align: left; }
  .font-bold { font-weight: bold; }
  
  /* Totals section */
  .totals-section {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 20px;
  }
  
  .totals-table {
    width: 320px;
    background-color: #f8f9fa;
    padding: 15px;
    border-radius: 4px;
    border: 1px solid #dee2e6;
  }
  
  .totals-row {
    display: flex;
    justify-content: space-between;
    padding: 3px 0;
  }
  
  .totals-row.border-top {
    border-top: 1px solid #dee2e6;
    margin-top: 12px;
    padding-top: 12px;
  }
  
  .label-col {
    color: #333;
    font-size: 11px;
  }
  
  .amount-col {
    font-weight: 500;
    color: #000;
    font-size: 11px;
    font-family: 'Courier New', monospace;
  }
  
  .total-row .label-col {
    font-size: 14px;
    font-weight: 600;
    color: #000;
  }
  
  .total-row .amount-col {
    font-size: 14px;
    font-weight: bold;
    color: #000;
  }
  
  .gst-breakdown-title {
    font-size: 10px;
    font-weight: 500;
    color: #000;
    margin-bottom: 3px;
  }
  
  .gst-breakdown-row {
    display: flex;
    justify-content: space-between;
    padding: 1px 0;
  }
  
  .gst-breakdown-row .label-col {
    font-size: 9px;
    color: #333;
  }
  
  .gst-breakdown-row .amount-col {
    font-size: 9px;
    font-weight: 500;
    color: #000;
  }
  
  /* Notes and terms */
  .notes-terms-section {
    margin: 20px 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }
  
  .notes-box, .terms-box {
    border: 1px solid #dee2e6;
    padding: 10px;
    border-radius: 4px;
    background-color: #f8f9fa;
  }
  
  .notes-title, .terms-title {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 6px;
    color: #000;
  }
  
  .notes-content, .terms-content {
    font-size: 11px;
    line-height: 1.3;
    color: #333;
    white-space: pre-wrap;
  }
  
  /* Footer */
  .invoice-footer {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid #dee2e6;
    text-align: center;
  }
  
  .footer-text {
    font-size: 11px;
    color: #333;
    line-height: 1.3;
  }
  
  /* Print optimizations */
  @media print {
    body {
      margin: 0;
      padding: 8px;
      font-size: 10px;
    }
    
    .print-content {
      margin: 0;
      padding: 12px;
      max-width: none;
    }
    
    .invoice-header {
      page-break-inside: avoid;
      margin-bottom: 15px;
    }
    
    .invoice-title {
      font-size: 20px;
    }
    
    .vendor-name {
      font-size: 14px;
    }
    
    .items-table {
      page-break-inside: auto;
      font-size: 9px;
      margin: 8px 0;
    }
    
    .items-table th {
      font-size: 8px;
      padding: 4px 2px;
    }
    
    .items-table td {
      font-size: 9px;
      padding: 4px 2px;
    }
    
    .item-description {
      font-size: 9px;
    }
    
    .item-details {
      font-size: 7px;
    }
    
    .totals-section {
      page-break-inside: avoid;
      margin-bottom: 15px;
    }
    
    .totals-table {
      width: 280px;
      padding: 12px;
    }
    
    .notes-terms-section {
      grid-template-columns: 1fr;
      gap: 10px;
      margin: 15px 0;
    }
    
    .notes-box, .terms-box {
      padding: 8px;
    }
    
    .invoice-footer {
      margin-top: 15px;
      padding-top: 10px;
    }
  }
</style>`;

export const generateInvoicePrintHTML = (invoice, vendorProfile, isPurchase = false) => {
  const discountAmount = invoice.discount ? (invoice.subtotal * parseFloat(invoice.discount)) / 100 : 0;
  const gstBreakdown = isPurchase ? getPurchaseGstBreakdown(invoice) : getSalesGstBreakdown(invoice);
  const invoiceTypeField = isPurchase ? 'gstType' : 'invoiceType';
  const isGSTInvoice = invoice[invoiceTypeField] === 'with_gst';
  
  return `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${isPurchase ? 'Purchase ' : ''}Invoice ${invoice.invoiceNumber}</title>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        ${getOptimizedPrintCSS()}
      </head>
      <body>
        <div class="print-content">
          <!-- Invoice Header -->
          <div class="invoice-header">
            <div>
              <div class="invoice-title">${isPurchase ? 'PURCHASE INVOICE' : 'INVOICE'}</div>
              <div class="invoice-meta">
                <div><strong>Invoice #:</strong> ${invoice.invoiceNumber}</div>
                <div><strong>${isPurchase ? 'Purchase ' : ''}Date:</strong> ${formatDate(isPurchase ? invoice.purchaseDate : invoice.createdAt)}</div>
                ${isPurchase && invoice.paymentTerms ? `<div><strong>Payment Terms:</strong> ${getPaymentTermsDisplay(invoice.paymentTerms, !isPurchase)}</div>` : ''}
                ${!isPurchase ? '<div><strong>Status:</strong> <span style="background-color: #dcfce7; color: #166534; padding: 2px 6px; border-radius: 12px; font-size: 9px;">PAID</span></div>' : ''}
              </div>
            </div>
            
            ${isGSTInvoice ? `
            <div class="vendor-details">
              <div class="vendor-name">${vendorProfile?.shopName || 'Your Shop Name'}</div>
              <div class="vendor-info">
                ${vendorProfile?.address || 'Shop Address'}<br>
                ${vendorProfile?.city || 'City'}, ${vendorProfile?.state || 'State'} ${vendorProfile?.pincode || 'PIN'}<br>
                Phone: ${vendorProfile?.phone || 'N/A'}<br>
                Email: ${vendorProfile?.email || 'N/A'}
                ${vendorProfile?.gstNumber ? '<br>GST: ' + vendorProfile.gstNumber : ''}
              </div>
            </div>
            ` : ''}
          </div>

          <!-- Customer/Supplier Details -->
          <div class="details-section">
            <div class="section-title">${isPurchase ? 'Supplier Details:' : 'Bill To:'}</div>
            <div class="details-box">
              <div class="customer-name">${isPurchase ? invoice.supplierName : invoice.customerName}</div>
              <div class="customer-info">
                ${isPurchase ? (invoice.supplierEmail ? invoice.supplierEmail + '<br>' : '') : (invoice.customerEmail ? invoice.customerEmail + '<br>' : '')}
                ${isPurchase ? (invoice.supplierContact ? invoice.supplierContact + '<br>' : '') : (invoice.customerPhone ? invoice.customerPhone + '<br>' : '')}
                ${isGSTInvoice && (isPurchase ? invoice.supplierGST : invoice.customerGST) ? 'GST: ' + (isPurchase ? invoice.supplierGST : invoice.customerGST) + '<br>' : ''}
                ${isPurchase ? (invoice.supplierAddress ? '<br>' + invoice.supplierAddress : '') : (invoice.customerAddress ? '<br>' + invoice.customerAddress : '')}
              </div>
            </div>
          </div>

          <!-- Items Table -->
          <div class="items-section">
            <h4 style="font-size: 14px; font-weight: 600; margin-bottom: 12px; color: #000;">${isPurchase ? 'Purchase' : 'Invoice'} Items</h4>
            <table class="items-table">
              <thead>
                <tr>
                  <th class="text-left" style="width: 25%;">Description</th>
                  <th class="text-center" style="width: 8%;">HSN</th>
                  <th class="text-center" style="width: 6%;">Qty</th>
                  <th class="text-right" style="width: 10%;">Rate (₹)</th>
                  ${isGSTInvoice ? `
                    <th class="text-center" style="width: 7%;">GST (%)</th>
                    <th class="text-right" style="width: 9%;">CGST (₹)</th>
                    <th class="text-right" style="width: 9%;">SGST (₹)</th>
                  ` : ''}
                  <th class="text-right" style="width: 11%;">Amount (₹)</th>
                  <th class="text-right" style="width: 15%;">Total (₹)</th>
                </tr>
              </thead>
              <tbody>
                ${(invoice.items || []).map((item, index) => {
                  const discountPercentage = parseFloat(invoice.discount) || 0;
                  const itemDiscountAmount = isPurchase ? 0 : (item.amount * discountPercentage) / 100;
                  const itemTaxableAmount = (item.amount || 0) - itemDiscountAmount;
                  const { cgstAmount, sgstAmount, totalGSTAmount } = isGSTInvoice ? 
                    calculateCGSTSGST(isPurchase ? item.amount : itemTaxableAmount, item.gstRate || 0) : 
                    { cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
                  
                  // Calculate final item total
                  let finalItemTotal;
                  if (isPurchase) {
                    finalItemTotal = (item.amount || 0) + cgstAmount + sgstAmount;
                  } else {
                    finalItemTotal = itemTaxableAmount + cgstAmount + sgstAmount;
                  }

                  return `
                    <tr>
                      <td class="text-left">
                        <div class="item-description">${isPurchase ? item.productName : item.name}</div>
                        ${item.productId ? '<div class="item-details">ID: ' + item.productId + '</div>' : ''}
                      </td>
                      <td class="text-center">${item.hsnCode || 'N/A'}</td>
                      <td class="text-center">${item.quantity}</td>
                      <td class="text-right">${formatCurrency(item.rate).replace('₹', '').trim()}</td>
                      ${isGSTInvoice ? `
                        <td class="text-center">${item.gstRate || 0}%</td>
                        <td class="text-right">${formatCurrency(cgstAmount).replace('₹', '').trim()}</td>
                        <td class="text-right">${formatCurrency(sgstAmount).replace('₹', '').trim()}</td>
                      ` : ''}
                      <td class="text-right">${formatCurrency(item.amount).replace('₹', '').trim()}</td>
                      <td class="text-right font-bold">${formatCurrency(finalItemTotal).replace('₹', '').trim()}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>

          <!-- Totals -->
          <div class="totals-section">
            <div class="totals-table">
              <div class="totals-row">
                <span class="label-col">Subtotal:</span>
                <span class="amount-col">${formatCurrency(invoice.subtotal || 0)}</span>
              </div>
              
              ${!isPurchase && invoice.discount && parseFloat(invoice.discount) > 0 ? `
              <div class="totals-row">
                <span class="label-col">Discount (${invoice.discount}%):</span>
                <span class="amount-col">-${formatCurrency(discountAmount)}</span>
              </div>
              ` : ''}
              
              ${isGSTInvoice && Object.keys(gstBreakdown).length > 0 ? `
                <div style="border-top: 1px solid #dee2e6; margin-top: 6px; padding-top: 6px;">
                  <div class="gst-breakdown-title">GST Breakdown:</div>
                  ${Object.entries(gstBreakdown).map(([rate, data]) => `
                    <div class="gst-breakdown-row">
                      <span class="label-col">CGST @ ${rate / 2}%:</span>
                      <span class="amount-col">${formatCurrency(data.cgstAmount)}</span>
                    </div>
                    <div class="gst-breakdown-row">
                      <span class="label-col">SGST @ ${rate / 2}%:</span>
                      <span class="amount-col">${formatCurrency(data.sgstAmount)}</span>
                    </div>
                  `).join('')}
                  <div class="totals-row" style="border-top: 1px solid #dee2e6; margin-top: 3px; padding-top: 3px;">
                    <span class="label-col" style="font-weight: 500;">Total GST:</span>
                    <span class="amount-col">${formatCurrency(isPurchase ? invoice.totalGstAmount || 0 : invoice.totalTaxAmount || 0)}</span>
                  </div>
                </div>
              ` : ''}
              
              <div class="totals-row border-top total-row">
                <span class="label-col">Total:</span>
                <span class="amount-col">${formatCurrency(
                  isPurchase ? 
                    (invoice.totalAmount || 0) : 
                    ((invoice.subtotal || 0) - discountAmount + (invoice.totalTaxAmount || 0))
                )}</span>
              </div>
            </div>
          </div>

          ${(invoice.notes || invoice.terms) ? `
          <!-- Notes and Terms -->
          <div class="notes-terms-section">
            ${invoice.notes ? `
            <div class="notes-box">
              <div class="notes-title">Notes:</div>
              <div class="notes-content">${invoice.notes}</div>
            </div>
            ` : ''}
            
            ${invoice.terms ? `
            <div class="terms-box">
              <div class="terms-title">Terms & Conditions:</div>
              <div class="terms-content">${invoice.terms}</div>
            </div>
            ` : ''}
          </div>
          ` : ''}

          <!-- Footer -->
          <div class="invoice-footer">
            <div class="footer-text">
              <p><strong>Thank you for your business!</strong></p>
              <p>This is a computer generated ${isPurchase ? 'purchase ' : ''}invoice.</p>
            </div>
          </div>
        </div>

        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                if (window.opener) {
                  window.close();
                }
              }, 1000);
            }, 500);
          };
          
          window.onafterprint = function() {
            if (window.opener) {
              window.close();
            }
          };
        </script>
      </body>
    </html>
  `;
};

export const printInvoice = (invoice, vendorProfile) => {
  const printContent = generateInvoicePrintHTML(invoice, vendorProfile, false);
  const printWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');
  
  if (!printWindow) {
    alert('Please allow popups for this website to enable printing.');
    return false;
  }
  
  printWindow.document.write(printContent);
  printWindow.document.close();
  
  return true;
};

export const printPurchaseInvoice = (invoice, vendorProfile) => {
  const printContent = generateInvoicePrintHTML(invoice, vendorProfile, true);
  const printWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');
  
  if (!printWindow) {
    alert('Please allow popups for this website to enable printing.');
    return false;
  }
  
  printWindow.document.write(printContent);
  printWindow.document.close();
  
  return true;
};

export const downloadInvoiceAsPDF = (invoice, vendorProfile) => {
  return printInvoice(invoice, vendorProfile);
};

export const downloadPurchaseInvoiceAsPDF = (invoice, vendorProfile) => {
  return printPurchaseInvoice(invoice, vendorProfile);
};